import React from "react";
/* =========================================================
   珂玺资本 — 拼图拖动人机验证（纯前端）
   拖动滑块把缺口碎片对齐到目标位置即通过；不拦服务器。
   SliderCaptcha 暴露到 window，用于注册/登录表单。
   ========================================================= */

const { useState, useRef, useEffect, useCallback } = React;

const W = 280;      // 画布逻辑宽（= CSS 宽，1:1，避免缩放换算）
const H = 140;      // 画布高
const SIZE = 40;    // 碎片边长
const PAD = 3;      // 碎片画布左右留白，避免描边被裁
const PW = SIZE + PAD * 2; // 碎片画布实际宽
const TOL = 6;      // 通过容差（px）

// 圆角方块路径
function piecePath(ctx, x, y) {
  const r = 6;
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + SIZE, y, x + SIZE, y + SIZE, r);
  ctx.arcTo(x + SIZE, y + SIZE, x, y + SIZE, r);
  ctx.arcTo(x, y + SIZE, x, y, r);
  ctx.arcTo(x, y, x + SIZE, y, r);
  ctx.closePath();
}

// 把任意 CSS 颜色字符串解析成 [r,g,b]（用一次性 canvas 让浏览器归一化）
let _probe = null;
function parseRGB(str, fallback) {
  try {
    if (!_probe) _probe = document.createElement("canvas").getContext("2d");
    _probe.fillStyle = "#000";
    _probe.fillStyle = str; // 非法值会被忽略，保留上一次
    const v = _probe.fillStyle; // "#rrggbb" 或 "rgba(...)"
    if (v.startsWith("#")) {
      const n = parseInt(v.slice(1), 16);
      return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
    }
    const m = v.match(/(\d+(?:\.\d+)?)/g);
    if (m) return [+m[0], +m[1], +m[2]];
  } catch {}
  return fallback;
}

// 读取当前主题的关键色（随主题/强调色切换而变）
function readTheme() {
  const cs = getComputedStyle(document.documentElement);
  const g = (name, fb) => parseRGB(cs.getPropertyValue(name).trim(), fb);
  return {
    bg1: g("--bg-1", [245, 241, 232]),   // 画布底
    bg2: g("--bg-2", [236, 228, 211]),
    bg0: g("--bg-0", [237, 232, 216]),   // 缺口填充
    gold: g("--gold", [201, 168, 107]),  // 强调色
    fg: g("--ivory", [43, 58, 90]),      // 前景（浅主题=深，深主题=浅）→ 做对比线条/描边
  };
}
const rgba = (c, a) => `rgba(${c[0]},${c[1]},${c[2]},${a})`;

// 在背景上画一个缺口（真缺口 + 干扰缺口同款样式），用主题色
function drawGap(bg, x, y, T) {
  bg.save();
  piecePath(bg, x, y);
  bg.fillStyle = rgba(T.bg0, 0.72);
  bg.fill();
  bg.strokeStyle = rgba(T.fg, 0.18);   // 内侧浅阴影（前景色淡）
  bg.lineWidth = 3;
  bg.stroke();
  bg.strokeStyle = rgba(T.gold, 0.9);  // 强调色描边
  bg.lineWidth = 1.5;
  bg.stroke();
  bg.restore();
}

// 画背景（随主题：底色用 --bg，强调用 --gold，线条用 --ivory）+ 缺口 + 抠出碎片
function drawScene(bgCanvas, pieceCanvas, gapX, gapY, decoyX, decoyY) {
  const T = readTheme();
  const bg = bgCanvas.getContext("2d");
  const pc = pieceCanvas.getContext("2d");
  bg.clearRect(0, 0, W, H);
  pc.clearRect(0, 0, PW, H);

  // 底：--bg-1 → --bg-2 渐变
  const grad = bg.createLinearGradient(0, 0, W, H);
  grad.addColorStop(0, rgba(T.bg1, 1));
  grad.addColorStop(1, rgba(T.bg2, 1));
  bg.fillStyle = grad;
  bg.fillRect(0, 0, W, H);

  // 柔和强调色同心弧线（右上发散）
  bg.lineWidth = 1;
  for (let r = 30; r < 340; r += 26) {
    bg.beginPath();
    bg.strokeStyle = rgba(T.gold, Math.max(0, 0.16 - r / 3200));
    bg.arc(W - 24, 18, r, 0, Math.PI * 2);
    bg.stroke();
  }
  // 细斜纹理（前景色极淡）
  bg.strokeStyle = rgba(T.fg, 0.05);
  for (let i = -H; i < W; i += 16) {
    bg.beginPath();
    bg.moveTo(i, 0);
    bg.lineTo(i + H, H);
    bg.stroke();
  }
  // 散落强调色点
  for (let i = 0; i < 14; i++) {
    bg.fillStyle = rgba(T.gold, 0.5 - (i % 5) * 0.06);
    bg.beginPath();
    bg.arc((i * 59 + 12) % W, (i * 41 + 20) % H, 1.4, 0, Math.PI * 2);
    bg.fill();
  }

  // 抠碎片：碎片实体绘制在画布内偏移 PAD 处（留描边余量），纵向同 gapY，取背景缺口处图像
  pc.save();
  piecePath(pc, PAD, gapY);
  pc.clip();
  pc.drawImage(bgCanvas, -gapX + PAD, 0);
  pc.restore();
  // 碎片描边：前景色细边 + 内侧高光（强调色），浮起感
  pc.save();
  piecePath(pc, PAD, gapY);
  pc.strokeStyle = rgba(T.fg, 0.55);
  pc.lineWidth = 1.5;
  pc.stroke();
  pc.restore();
  pc.save();
  piecePath(pc, PAD + 0.75, gapY + 0.75);
  pc.strokeStyle = rgba(T.gold, 0.55);
  pc.lineWidth = 1;
  pc.stroke();
  pc.restore();

  // 干扰缺口（同款样式，不同位置/高度）——先画，真缺口压其上（视觉无差别，仅增加机器人识别难度）
  if (decoyX != null) drawGap(bg, decoyX, decoyY, T);
  // 真缺口：碎片需对齐此处才通过
  drawGap(bg, gapX, gapY, T);
}

function SliderCaptcha({ lang, passed, onPass }) {
  const isZh = lang === "cn";
  const bgRef = useRef(null);
  const pieceRef = useRef(null);
  const [gapX, setGapX] = useState(0);
  const [slide, setSlide] = useState(0);    // 碎片/滑块横向位移（0..W-SIZE）
  const dragRef = useRef(null);
  const sceneRef = useRef(null);            // 当前题坐标，用于主题切换时原样重绘

  // 用当前坐标重绘（不换题）——主题/强调色切换时调用
  const redraw = useCallback(() => {
    const s = sceneRef.current;
    if (s && bgRef.current && pieceRef.current) drawScene(bgRef.current, pieceRef.current, s.gx, s.gy, s.dx, s.dy);
  }, []);

  const init = useCallback(() => {
    const gx = Math.round(SIZE * 1.5 + Math.random() * (W - SIZE * 2.5));
    const gy = Math.round(Math.random() * (H - SIZE - 12) + 6);
    // 干扰缺口：在真缺口左右另找一处，X 间距≥SIZE+16，Y 与真缺口错开≥SIZE/2（碎片只与真缺口同高，便于人眼区分）
    let dx;
    for (let i = 0; i < 30; i++) {
      dx = Math.round(SIZE + Math.random() * (W - SIZE * 2));
      if (Math.abs(dx - gx) >= SIZE + 16) break;
    }
    let dy = Math.round(Math.random() * (H - SIZE - 12) + 6);
    if (Math.abs(dy - gy) < SIZE / 2) dy = gy + (gy < H / 2 ? SIZE / 2 + 8 : -(SIZE / 2 + 8));
    sceneRef.current = { gx, gy, dx, dy };
    setGapX(gx);
    setSlide(0);
    redraw();
  }, [redraw]);

  useEffect(() => { init(); }, [init]);

  // 主题/强调色切换：root 的 style 变化时用主题色原样重绘当前题
  useEffect(() => {
    const obs = new MutationObserver(() => redraw());
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ["style"] });
    obs.observe(document.body, { attributes: true, attributeFilter: ["data-theme", "data-mood"] });
    return () => obs.disconnect();
  }, [redraw]);

  const trackW = W - SIZE;
  const onDown = (e, clientX) => {
    if (passed) return;
    if (e && e.preventDefault) e.preventDefault(); // 阻止拖拽起选文本
    dragRef.current = { startX: clientX, base: slide };
  };
  const onMove = useCallback((clientX) => {
    if (!dragRef.current || passed) return;
    let next = dragRef.current.base + (clientX - dragRef.current.startX);
    setSlide(Math.max(0, Math.min(trackW, next)));
  }, [passed, trackW]);
  const onUp = useCallback(() => {
    if (!dragRef.current || passed) return;
    dragRef.current = null;
    if (Math.abs(slide - gapX) <= TOL) { setSlide(gapX); onPass && onPass(); }
    else setTimeout(() => init(), 120);
  }, [slide, gapX, passed, onPass, init]);

  useEffect(() => {
    const mm = (e) => onMove(e.clientX);
    const tm = (e) => { if (e.touches[0]) onMove(e.touches[0].clientX); };
    const end = () => onUp();
    window.addEventListener("mousemove", mm);
    window.addEventListener("mouseup", end);
    window.addEventListener("touchmove", tm, { passive: true });
    window.addEventListener("touchend", end);
    return () => {
      window.removeEventListener("mousemove", mm);
      window.removeEventListener("mouseup", end);
      window.removeEventListener("touchmove", tm);
      window.removeEventListener("touchend", end);
    };
  }, [onMove, onUp]);

  // 画布上叠加的元素改用主题变量（随主题/强调色变化），与 canvas 内绘制保持同一套配色
  const knobX = (slide / trackW) * (W - 44); // 与滑块宽度 44 对应
  return (
    <div style={{ marginBottom: 16, userSelect: "none" }}>
      <div style={{ position: "relative", width: W, maxWidth: "100%", margin: "0 auto",
                    borderRadius: 8, overflow: "hidden", border: "1px solid var(--gold-dim)",
                    boxShadow: "0 6px 18px rgba(0,0,0,0.12)" }}>
        <canvas ref={bgRef} width={W} height={H}
          style={{ width: W, maxWidth: "100%", height: "auto", display: "block" }} />
        <canvas ref={pieceRef} width={PW} height={H}
          style={{ position: "absolute", top: 0, left: 0, width: PW, height: H,
                   transform: `translateX(${slide - PAD}px)`, pointerEvents: "none",
                   filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.28))" }} />
      </div>
      {/* 滑轨（外层不裁切，滑块圆形可完整浮出）*/}
      <div style={{ position: "relative", width: W, maxWidth: "100%", margin: "14px auto 0", height: 40 }}>
        {/* 轨道 + 进度（内层单独裁切，只裁进度不裁滑块）*/}
        <div style={{ position: "absolute", inset: 0, background: "var(--bg-1)", borderRadius: 20,
                      border: "1px solid var(--gold-dim)", overflow: "hidden" }}>
          <div style={{ position: "absolute", top: 0, left: 0, height: "100%",
                        width: knobX + 22, background: passed ? "var(--gold)" : "var(--gold-dim)", opacity: passed ? 1 : 0.4,
                        transition: "background .2s, opacity .2s" }} />
          <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: 12, color: passed ? "var(--bg-1)" : "var(--mute)", pointerEvents: "none", letterSpacing: "0.06em",
                        fontFamily: "var(--f-serif-cn)",
                        userSelect: "none", WebkitUserSelect: "none", MozUserSelect: "none" }}>
            {passed ? (isZh ? "验证通过" : "Verified") : (isZh ? "拖动滑块完成拼图" : "Slide to complete the puzzle")}
          </div>
        </div>
        {/* 滑块：比轨道略大、上下各浮出 2px，圆形完整不被裁 */}
        <div
          onMouseDown={(e) => onDown(e, e.clientX)}
          onTouchStart={(e) => e.touches[0] && onDown(e, e.touches[0].clientX)}
          onDragStart={(e) => e.preventDefault()}
          role="button"
          aria-label={isZh ? "人机验证滑块" : "Captcha slider"}
          style={{
            position: "absolute", top: -2, height: 44, width: 44, borderRadius: "50%",
            transform: `translateX(${knobX}px)`,
            background: passed ? "var(--gold)" : "var(--bg-3)",
            border: "1px solid var(--gold)",
            boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
            cursor: passed ? "default" : "grab",
            display: "flex", alignItems: "center", justifyContent: "center",
            color: passed ? "var(--bg-1)" : "var(--gold)", fontSize: 16,
            userSelect: "none", WebkitUserSelect: "none", MozUserSelect: "none",
            WebkitTouchCallout: "none",
          }}
        >
          {passed ? "✓" : "→"}
        </div>
      </div>
    </div>
  );
}

Object.assign(window, { SliderCaptcha });
